import { useEffect, useState } from 'react';
import { Alert, Button, Chip, Stack, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * STAN ŚWIEŻOŚCI DANYCH — dla ekranów, na których stare dane są gorsze niż ich brak.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ┌─ PO CO TO ISTNIEJE ─────────────────────────────────────────────────────────────────────────┐
 * │ Ekran odświeżany w tle wygląda tak samo, gdy działa, i tak samo, gdy odświeżanie od kwadransa│
 * │ się nie udaje. Kucharz patrzy wtedy na liczby sprzed godziny w przekonaniu, że są bieżące.   │
 * │                                                                                              │
 * │ Dlatego: widoczny czas ostatniego udanego pobrania, ręczne odświeżenie i JAWNY stan          │
 * │ nieświeżości, gdy próby zawodzą.                                                             │
 * └──────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * Nakładających się zapytań nie ma z założenia: warstwa zapytań scala równoległe wywołania
 * tego samego klucza, a odpytywanie w tle zatrzymuje się, gdy karta jest niewidoczna.
 */

/** Po tylu minutach bez udanego pobrania dane przestają uchodzić za bieżące. */
const PROG_NIESWIEZOSCI_MIN = 5;

function ileMinut(od: number | undefined, teraz: number): number | null {
  if (!od) return null;
  return Math.floor((teraz - od) / 60_000);
}

/**
 * Bieżący czas jako STAN, nie jako odczyt zegara w trakcie renderu.
 *
 * Dwa powody. Po pierwsze, czytanie zegara podczas renderowania jest nieczyste i React
 * słusznie na to narzeka. Po drugie — i ważniejsze — etykieta „aktualne: 2 min temu" musi
 * SAMA się starzeć. Gdyby czas był odczytywany tylko przy przerysowaniu, ekran bez ruchu
 * pokazywałby „przed chwilą" dowolnie długo.
 */
function useTeraz(wstrzykniety?: number): number {
  const [teraz, setTeraz] = useState(() => wstrzykniety ?? Date.now());

  useEffect(() => {
    if (wstrzykniety !== undefined) return;                 // testy podają czas wprost
    const id = setInterval(() => setTeraz(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [wstrzykniety]);

  return wstrzykniety ?? teraz;
}

export function StanOdswiezania({
  pobranoO,
  odswiezanie,
  bladOdswiezania,
  nieudanychProb = 0,
  onOdswiez,
  teraz: terazWstrzykniety,
}: {
  /** Znacznik ostatniego UDANEGO pobrania (`dataUpdatedAt`). */
  pobranoO: number | undefined;
  odswiezanie: boolean;
  bladOdswiezania: boolean;
  nieudanychProb?: number;
  onOdswiez: () => void;
  /** Wstrzykiwany czas — testy nie mogą zależeć od zegara. */
  teraz?: number;
}) {
  const teraz = useTeraz(terazWstrzykniety);
  const minut = ileMinut(pobranoO, teraz);
  const nieswieze = minut !== null && minut >= PROG_NIESWIEZOSCI_MIN;
  const powtarzalnaAwaria = bladOdswiezania && nieudanychProb >= 2;

  const opis = minut === null ? 'jeszcze nie pobrano'
    : minut === 0 ? 'przed chwilą'
    : minut === 1 ? 'minutę temu'
    : `${minut} min temu`;

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
        <Tooltip title="Dane odświeżają się co minutę i po powrocie do okna.">
          <Chip
            size="small"
            variant="outlined"
            color={nieswieze ? 'warning' : 'default'}
            label={`aktualne: ${opis}`}
          />
        </Tooltip>

        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={onOdswiez}
          disabled={odswiezanie}
        >
          {odswiezanie ? 'Odświeżam…' : 'Odśwież'}
        </Button>
      </Stack>

      {/*
        Awaria JEDNEJ próby nie jest jeszcze wiadomością — sieć w kuchni bywa kapryśna,
        a następna próba za minutę zwykle się udaje. Dopiero powtarzalna awaria znaczy,
        że to, co widać, przestało nadążać za rzeczywistością.
      */}
      {powtarzalnaAwaria && (
        <Alert severity="warning">
          <strong>Odświeżanie nie działa.</strong> Widoczne liczby pochodzą z ostatniego udanego
          pobrania ({opis}) i mogą już nie być aktualne. Sprawdź połączenie albo odśwież ręcznie.
        </Alert>
      )}

      {!powtarzalnaAwaria && nieswieze && (
        <Typography variant="caption" sx={{ color: 'warning.main' }}>
          Dane nie odświeżyły się od {minut} min.
        </Typography>
      )}
    </Stack>
  );
}

export { PROG_NIESWIEZOSCI_MIN };
